import { Toast } from '@base-ui/react';
import { ReactNode } from 'react';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

export enum EToastVariant {
  Alert = 'alert',
  Toast = 'toast'
}

export enum EToastType {
  REGULAR = 'regular',
  WARNING = 'warning',
  ERROR = 'error',
  SUCCESS = 'success',
  LOADING = 'loading'
}

export interface IToastOpts {
  id?: string;
  content: ReactNode;
  title?: ReactNode;
  type?: EToastType;
  onCancel?: () => void;
  onCancelText?: string;
  timeout?: number;
  variant?: EToastVariant;
}

/**
 * 全局唯一的 toast 管理器实例
 */
export const toastManager = Toast.createToastManager();

export interface IConfirmEntry {
  /** 队列内的唯一标识，由 confirm-service 生成且不复用 */
  id: string;
  /**
   * 正文，只允许字符串。字符串按 markdown 渲染，所以加粗、换行这类富文本
   * 与纯文本一样走这条队列，不需要 ReactNode。
   *
   * 只有需要内嵌交互控件（复选框、输入框等自身持有状态的元素）的弹窗禁止走
   * 队列：队列项是快照，受控控件的 value 会永远停在入队那一刻，只能靠闭包突变
   * 把用户的选择带出来。这类弹窗应当基于 `widgets/confirm` 的 `ConfirmDialog`
   * 派生业务子组件，在组件自己的 render 里构造内容，参考 `DeleteFolderDialog`。
   */
  content: string;
  contentTitle?: string;
  /** 主按钮文案，缺省为 `common.confirm` */
  confirmText?: string;
  /** 取消按钮文案，缺省为 `common.cancel` */
  cancelText?: string;
  hideCancel?: boolean;
  /** 主按钮改用危险色，用于删除这类不可逆动作。缺省是警示色 */
  danger?: boolean;
  /** 正文改用左对齐的灰底块。有标题时必然是灰底块，无标题时缺省为居中纯文本 */
  boxed?: boolean;
}

/**
 * 弹出确认框的入参。
 *
 * 结算回调不进 store，由 `widgets/confirm/confirm-service` 持有，
 * store 只保留渲染所需的数据。
 */
export type TConfirmOpts = Omit<IConfirmEntry, 'id'> & {
  onConfirm?: (confirmed: boolean) => void;
};

interface IConfirmState {
  /**
   * 待处理的确认框，FIFO，渲染层只渲染队首。
   *
   * 用队列而非单槽：单槽下后一次弹出会覆盖前一个的结算回调，
   * 使前一个 Promise 永不结算，await 它的调用方（含内核 Operation）永久挂起。
   */
  queue: IConfirmEntry[];
}

interface IConfirmActions {
  enqueueConfirm: (entry: IConfirmEntry) => void;
  dequeueConfirm: (id: string) => void;
}

/**
 * 这个 store 刻意不套 immer，与同文件的 `useLoadingStore` 不同：
 * 两个 action 都是整体替换，没有嵌套 draft 突变，immer 在此零收益，
 * 而它的 autoFreeze 会深冻结写进 state 的对象。
 */
export const useConfirmStore = create<IConfirmState & IConfirmActions>()((set) => ({
  queue: [],
  enqueueConfirm: (entry) => set((state) => ({ queue: [...state.queue, entry] })),
  dequeueConfirm: (id) => set((state) => ({ queue: state.queue.filter((item) => item.id !== id) }))
}));

interface ILoadingState {
  actived: boolean;
  pseudo?: boolean;
  content?: string;
  variant?: 'overlay' | 'transparent';
  progress?: number;
  duration?: number;
  hideProgressBar?: boolean;
}

export type TLoadingOpts = Omit<ILoadingState, 'actived'>;

interface ILoadingActions {
  startLoading: (options: TLoadingOpts) => void;
  updateLoading: (progress: number) => void;
  finishLoading: () => void;
}

export const useLoadingStore = create<ILoadingState & ILoadingActions>()(
  immer((set) => ({
    actived: false,
    startLoading: (options) =>
      set((state) => {
        state.actived = true;
        state.pseudo = options.pseudo;
        state.content = options.content;
        state.variant = options.variant;
        state.progress = options.progress;
        state.duration = options.duration;
        state.hideProgressBar = options.hideProgressBar;
      }),
    updateLoading: (progress) =>
      set((state) => {
        if (state.actived) {
          state.progress = progress;
        }
      }),
    finishLoading: () =>
      set((state) => {
        state.actived = false;
        state.pseudo = false;
      })
  }))
);
